import React, { useCallback, useMemo } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";

import { MachineConfiguration, MachineQrCode } from "./machineEditor.typing";
import { MachineConfirmationStage } from "./useMachineCreationConfirmation";

interface MachineQrCodePreviewProps {
  machine: MachineConfiguration;
  qrCode: MachineQrCode;
  stage: MachineConfirmationStage;
  signalReceived: boolean;
  attempts: number;
  onRetry: () => void;
  showEmailNotice?: boolean;
}

const panelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
  padding: "8px 0",
};

const waitingStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
  padding: "32px 0",
  color: "#d7c6d8",
  textAlign: "center",
};

const previewFrameStyle: React.CSSProperties = {
  backgroundColor: "#fff",
  borderRadius: "8px",
  padding: "12px",
  lineHeight: 0,
};

export const MachineQrCodePreview: React.FC<MachineQrCodePreviewProps> = ({
  machine,
  qrCode,
  stage,
  signalReceived,
  attempts,
  onRetry,
  showEmailNotice = false,
}) => {
  const imageSource = useMemo(
    () => `data:${qrCode.ContentType};base64,${qrCode.ImageBase64}`,
    [qrCode.ContentType, qrCode.ImageBase64]
  );

  const download = useCallback(() => {
    const link = document.createElement("a");
    link.href = imageSource;
    link.download = qrCode.FileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [imageSource, qrCode.FileName]);

  if (stage !== "confirmed") {
    return (
      <div style={waitingStyle}>
        {stage === "timedOut" ? (
          <>
            <div style={{ color: "#ffb4b4" }}>
              <strong>[{machine.MachineCode}] {machine.MachineName}</strong> was saved,
              but we could not confirm it is scannable yet.
            </div>
            <div style={{ fontSize: "13px" }}>
              Printing the code now risks a label that does not resolve. Check again
              before printing.
            </div>
            <Button variant="outlined" onClick={onRetry}>
              Check again
            </Button>
          </>
        ) : (
          <>
            <CircularProgress size={32} />
            <div>
              {signalReceived
                ? "Machine created. Confirming it can be scanned..."
                : "Waiting for the machine to be confirmed..."}
            </div>
            <div style={{ fontSize: "12px", opacity: 0.75 }}>
              Attempt {attempts}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      <div style={{ textAlign: "center" }}>
        <strong>
          [{machine.MachineCode}] {machine.MachineName}
        </strong>
        <div style={{ fontSize: "13px", opacity: 0.8 }}>
          {machine.GroupName} &middot;{" "}
          {machine.IsAvailable ? "Available" : "Excluded"}
        </div>
      </div>

      <div style={previewFrameStyle}>
        <img
          src={imageSource}
          alt={`QR code for ${machine.MachineCode} ${machine.MachineName}`}
          width={220}
          height={220}
          style={{ display: "block", imageRendering: "pixelated" }}
        />
      </div>

      <Button variant="contained" startIcon={<DownloadIcon />} onClick={download}>
        Save QR code
      </Button>

      {showEmailNotice && (
        <div style={{ fontSize: "12px", opacity: 0.75, textAlign: "center" }}>
          A copy has been emailed out, with a reminder to update the shared QR code file.
        </div>
      )}
    </div>
  );
};
